/**
 * Tabla del panel BA de precios (REQ-MINT-012 §2).
 *
 * Presentación pura del contrato ligero `GET /models/pricing`: modelo,
 * features (3 + «+N» con menú flotante de detalle) y precio cliente por
 * componente (entrada/salida/caché lectura/caché escritura). No calcula ni
 * estima precios: pinta lo que devuelve el backend. Sin columna «Enabled»:
 * habilitar modelos no es una acción de esta superficie.
 */

import type { LucideIcon } from 'lucide-react';
import {
  ArrowUpFromLine,
  Brain,
  CalendarClock,
  Eye,
  Gauge,
  MessageSquare,
  Mic,
  Sparkles,
  Video,
  Wrench,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { clientPriceComponent, formatDate, formatPriceUsd } from '../lib/pricing';
import type { PricingOffering } from '../types';

type Translate = (key: string, params?: Record<string, string | number>) => string;

interface Feature {
  key: string;
  label: string;
  detail: string;
  Icon: LucideIcon;
}

const PROVIDER_LABEL_KEYS: Record<string, string> = {
  azure_foundry: 'azureFoundry',
  aws_bedrock: 'awsBedrock',
  vertex_ai: 'vertexAi',
  voyage: 'voyage',
  anthropic: 'anthropic',
};

/**
 * Paleta de features — **un color distinto por tipo de feature** (guía de
 * estilos, REQ-UIP-003/ISS-706). Se parte de la paleta de gráficos del
 * dashboard (violeta/verde/ámbar) y se completa con tonos accesibles para
 * evitar que varias features compartan el mismo amarillo. Los iconos de
 * sección van en azul corporativo (`text-primary`).
 */
export const FEATURE_COLORS: Record<
  string,
  { icon: string; text: string; soft: string }
> = {
  reasoning: { icon: '#8884d8', text: '#5b4fb0', soft: 'rgba(136, 132, 216, 0.14)' },
  tools: { icon: '#82ca9d', text: '#2f7a52', soft: 'rgba(130, 202, 157, 0.16)' },
  vision: { icon: '#ffc658', text: '#8a5d00', soft: 'rgba(255, 198, 88, 0.18)' },
  audio: { icon: '#ff8042', text: '#a34a14', soft: 'rgba(255, 128, 66, 0.16)' },
  video: { icon: '#00c49f', text: '#007a63', soft: 'rgba(0, 196, 159, 0.16)' },
  text: { icon: '#0088fe', text: '#005bb5', soft: 'rgba(0, 136, 254, 0.14)' },
  context: { icon: '#a4de6c', text: '#4f7a1f', soft: 'rgba(164, 222, 108, 0.18)' },
  'max-output': { icon: '#83a6ed', text: '#3f5fae', soft: 'rgba(131, 166, 237, 0.16)' },
  lifecycle: { icon: '#e57373', text: '#a33a3a', soft: 'rgba(229, 115, 115, 0.16)' },
};

const DEFAULT_FEATURE_COLOR = {
  icon: '#6b7280',
  text: '#4b5563',
  soft: 'rgba(107, 114, 128, 0.14)',
};

function featureColor(key: string) {
  return FEATURE_COLORS[key] ?? DEFAULT_FEATURE_COLOR;
}

const LIFECYCLE_LABEL_KEYS: Record<string, string> = {
  ga: 'ga',
  preview: 'preview',
  deprecated: 'deprecated',
  retired: 'retired',
};

function compactTokens(value: number): string {
  if (value >= 1_000_000) return `${Math.round(value / 1_000_000)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(value);
}

function modalityList(capabilities: Record<string, unknown>): string[] {
  const raw = capabilities['modalities'];
  return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : [];
}

function providerLabel(t: Translate, provider: string): string {
  const key = PROVIDER_LABEL_KEYS[provider];
  return key ? t(`modelIntel.providers.${key}`) : provider;
}

function lifecycleLabel(t: Translate, status: string): string {
  const key = LIFECYCLE_LABEL_KEYS[status];
  return key ? t(`modelIntel.pricing.features.lifecycles.${key}`) : status;
}

/**
 * Ofertas no conversacionales (embeddings/rerankers) que sí se tarifan por
 * token pero no tienen features de chat. Se usa solo para el texto vacío:
 * «No aplica» en vez de «sin features publicadas». Es una heurística de
 * presentación; el backend sigue siendo la fuente de precio y capacidades.
 */
const NON_CHAT_MARKERS = ['embed', 'rerank', 'voyage-', 'bge-', 'e5-', 'gecko'];

function isNonChatOffering(item: PricingOffering): boolean {
  const haystack = `${item.offering.model_ref ?? ''} ${item.offering.provider_model_id}`.toLowerCase();
  return NON_CHAT_MARKERS.some((marker) => haystack.includes(marker));
}

/**
 * Features publicadas de una oferta. Se ordenan por relevancia para dejar las
 * tres primeras visibles y el resto en el menú «+N».
 */
function buildFeatures(
  t: Translate,
  capabilities: Record<string, unknown>,
  lifecycleStatus: string | null,
): Feature[] {
  const features: Feature[] = [];
  if (capabilities['reasoning'] === true) {
    features.push({
      key: 'reasoning',
      label: t('modelIntel.pricing.features.reasoning'),
      detail: t('modelIntel.pricing.features.reasoningDetail'),
      Icon: Brain,
    });
  }
  if (capabilities['tools'] === true) {
    features.push({
      key: 'tools',
      label: t('modelIntel.pricing.features.tools'),
      detail: t('modelIntel.pricing.features.toolsDetail'),
      Icon: Wrench,
    });
  }
  const modalities = modalityList(capabilities);
  if (modalities.includes('image')) {
    features.push({
      key: 'vision',
      label: t('modelIntel.pricing.features.vision'),
      detail: t('modelIntel.pricing.features.visionDetail'),
      Icon: Eye,
    });
  }
  if (modalities.includes('audio')) {
    features.push({
      key: 'audio',
      label: t('modelIntel.pricing.features.audio'),
      detail: t('modelIntel.pricing.features.audioDetail'),
      Icon: Mic,
    });
  }
  if (modalities.includes('video')) {
    features.push({
      key: 'video',
      label: t('modelIntel.pricing.features.video'),
      detail: t('modelIntel.pricing.features.videoDetail'),
      Icon: Video,
    });
  }
  if (modalities.includes('text')) {
    features.push({
      key: 'text',
      label: t('modelIntel.pricing.features.text'),
      detail: t('modelIntel.pricing.features.textDetail'),
      Icon: MessageSquare,
    });
  }
  const context = capabilities['context_window'];
  if (typeof context === 'number' && context > 0) {
    features.push({
      key: 'context',
      label: t('modelIntel.pricing.features.context', {
        value: compactTokens(context),
      }),
      detail: t('modelIntel.pricing.features.contextDetail', {
        value: context.toLocaleString(),
      }),
      Icon: Gauge,
    });
  }
  const maxOutput = capabilities['max_output_tokens'];
  if (typeof maxOutput === 'number' && maxOutput > 0) {
    features.push({
      key: 'max-output',
      label: t('modelIntel.pricing.features.maxOutput', {
        value: compactTokens(maxOutput),
      }),
      detail: t('modelIntel.pricing.features.maxOutputDetail', {
        value: maxOutput.toLocaleString(),
      }),
      Icon: ArrowUpFromLine,
    });
  }
  if (lifecycleStatus) {
    features.push({
      key: 'lifecycle',
      label: lifecycleLabel(t, lifecycleStatus),
      detail: t('modelIntel.pricing.features.lifecycleDetail', {
        value: lifecycleLabel(t, lifecycleStatus),
      }),
      Icon: CalendarClock,
    });
  }
  return features;
}

function FeatureChip({ feature }: { feature: Feature }) {
  const palette = featureColor(feature.key);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
      style={{
        color: palette.text,
        borderColor: `${palette.icon}66`,
        backgroundColor: palette.soft,
      }}
      title={feature.detail}
      data-testid={`pricing-feature-${feature.key}`}
    >
      <feature.Icon
        className="h-3 w-3"
        style={{ color: palette.icon }}
        aria-hidden="true"
      />
      {feature.label}
    </span>
  );
}

function FeaturesCell({
  capabilities,
  lifecycleStatus,
  notApplicable,
  rowKey,
}: {
  capabilities: Record<string, unknown>;
  lifecycleStatus: string | null;
  notApplicable: boolean;
  rowKey: string;
}) {
  const { t } = useI18n();
  const features = buildFeatures(t, capabilities, lifecycleStatus);
  if (features.length === 0) {
    return (
      <span className="text-xs text-muted-foreground" data-testid={`pricing-features-empty-${rowKey}`}>
        {t(notApplicable ? 'modelIntel.pricing.notApplicable' : 'modelIntel.pricing.noFeatures')}
      </span>
    );
  }
  const visible = features.slice(0, 3);
  const hidden = features.slice(3);
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`pricing-features-${rowKey}`}>
      {visible.map((feature) => (
        <FeatureChip key={feature.key} feature={feature} />
      ))}
      {hidden.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 rounded-full px-1.5 text-[11px] text-muted-foreground"
              data-testid={`pricing-features-more-${rowKey}`}
              aria-label={t('modelIntel.pricing.featuresMore', { count: hidden.length })}
            >
              +{hidden.length}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 p-2">
            <div className="mb-1 flex items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              {t('modelIntel.pricing.featuresTitle')}
            </div>
            <div className="space-y-1.5">
              {features.map((feature) => {
                const palette = featureColor(feature.key);
                return (
                  <div
                    key={feature.key}
                    className="flex items-start gap-2 rounded px-1 py-0.5"
                    data-testid={`pricing-feature-detail-${rowKey}-${feature.key}`}
                  >
                    <feature.Icon
                      className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
                      style={{ color: palette.icon }}
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <div className="text-xs font-medium" style={{ color: palette.text }}>
                        {feature.label}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {feature.detail}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function priceTitle(t: Translate, item: PricingOffering): string {
  const price = item.client_price;
  const parts: string[] = [];
  if (price?.source === 'platform') parts.push(t('modelIntel.price.defaultMargin'));
  if (price?.option_version != null) {
    parts.push(t('modelIntel.price.optionVersion', { version: price.option_version }));
  }
  if (price?.effective_from) {
    parts.push(t('modelIntel.price.validFrom', { date: formatDate(price.effective_from) }));
  }
  return parts.join(' · ');
}

function PriceCell({
  item,
  component,
}: {
  item: PricingOffering;
  component: 'input' | 'output' | 'cache_read' | 'cache_write';
}) {
  const { t } = useI18n();
  const value = clientPriceComponent(item.client_price, component);
  const key = item.offering.sheet_id;
  return (
    <span
      className={
        value === null
          ? 'text-xs text-muted-foreground'
          : 'text-sm tabular-nums text-foreground'
      }
      title={priceTitle(t, item)}
      data-testid={`pricing-${component}-${key}`}
    >
      {value === null ? '—' : formatPriceUsd(value)}
    </span>
  );
}

function ModelCell({ item }: { item: PricingOffering }) {
  const { t } = useI18n();
  const price = item.client_price;
  const subtitle = [
    providerLabel(t, item.offering.provider),
    item.offering.region,
    price?.effective_from
      ? t('modelIntel.price.validFrom', { date: formatDate(price.effective_from) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="min-w-0">
      <div className="truncate font-medium" data-testid={`pricing-model-${item.offering.sheet_id}`}>
        {item.offering.model_ref ?? item.offering.provider_model_id}
      </div>
      <div className="truncate font-mono text-[11px] text-muted-foreground">
        {item.offering.provider_model_id}
      </div>
      <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>
    </div>
  );
}

export interface PricingCatalogTableProps {
  items: PricingOffering[];
  emptyLabel: string;
}

export function PricingCatalogTable({ items, emptyLabel }: PricingCatalogTableProps) {
  const { t } = useI18n();

  return (
    <>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">{t('modelIntel.pricing.colModel')}</TableHead>
              <TableHead className="min-w-[220px]">{t('modelIntel.pricing.colFeatures')}</TableHead>
              <TableHead className="text-right">
                {t('modelIntel.pricing.colInput')}
                <span className="ml-1 font-normal text-muted-foreground">
                  {t('modelIntel.price.perMillion')}
                </span>
              </TableHead>
              <TableHead className="text-right">
                {t('modelIntel.pricing.colOutput')}
                <span className="ml-1 font-normal text-muted-foreground">
                  {t('modelIntel.price.perMillion')}
                </span>
              </TableHead>
              <TableHead className="text-right">
                {t('modelIntel.pricing.colCacheRead')}
                <span className="ml-1 font-normal text-muted-foreground">
                  {t('modelIntel.price.perMillion')}
                </span>
              </TableHead>
              <TableHead className="text-right">
                {t('modelIntel.pricing.colCacheWrite')}
                <span className="ml-1 font-normal text-muted-foreground">
                  {t('modelIntel.price.perMillion')}
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableEmpty colSpan={6} title={emptyLabel} />}
            {items.map((item) => {
              const key = item.offering.sheet_id;
              return (
                <TableRow key={key} data-testid={`pricing-row-${key}`}>
                  <TableCell>
                    <ModelCell item={item} />
                  </TableCell>
                  <TableCell>
                    <FeaturesCell
                      capabilities={item.capabilities}
                      lifecycleStatus={item.lifecycle_status}
                      notApplicable={isNonChatOffering(item)}
                      rowKey={key}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <PriceCell item={item} component="input" />
                  </TableCell>
                  <TableCell className="text-right">
                    <PriceCell item={item} component="output" />
                  </TableCell>
                  <TableCell className="text-right">
                    <PriceCell item={item} component="cache_read" />
                  </TableCell>
                  <TableCell className="text-right">
                    <PriceCell item={item} component="cache_write" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Card-view móvil (REQ-UIP-003): misma información, sin scroll horizontal. */}
      <div className="space-y-3 md:hidden">
        {items.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        )}
        {items.map((item) => {
          const key = item.offering.sheet_id;
          return (
            <div
              key={key}
              className="rounded-lg border border-border p-3"
              data-testid={`pricing-card-${key}`}
            >
              <ModelCell item={item} />
              <div className="mt-2">
                <FeaturesCell
                  capabilities={item.capabilities}
                  lifecycleStatus={item.lifecycle_status}
                  notApplicable={isNonChatOffering(item)}
                  rowKey={`card-${key}`}
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {(
                  [
                    ['input', t('modelIntel.pricing.colInput')],
                    ['output', t('modelIntel.pricing.colOutput')],
                    ['cache_read', t('modelIntel.pricing.colCacheRead')],
                    ['cache_write', t('modelIntel.pricing.colCacheWrite')],
                  ] as const
                ).map(([component, label]) => (
                  <div key={component} className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">{label}</span>
                    <PriceCell item={item} component={component} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
